/**
 * ActivityEditTutorial — Onboarding spotlight (Steps 7–10) for ActivityEditPage
 *
 * Changes from v1:
 *  - Full-page click blocker (no form entry during tutorial)
 *  - Step 9 targets the Registration Form section
 *  - "Close and Return" closes tab + focuses opener
 *  - Total steps = 11
 */
import { useState, useEffect } from 'react';
import { trackTutorialSession } from '../api/api';

const TOTAL_STEPS = 11;
const TOOLTIP_W = 268;

const STEP_CFG = {
  7: {
    selector: '[data-testid="edit-happening-on"]',
    text: 'When is the activity happening',
    cta: 'Next',
  },
  8: {
    selector: '[data-testid="edit-happening-at"]',
    text: 'Where is the activity happening. If online, say "online"',
    cta: 'Next',
  },
  9: {
    selector: '[data-testid="edit-reg-section"]',
    text: 'Easily create a Registration Form and download registered user details. We\'ve set it up with Karmana Registration — you can customise the fields or switch to an external link.',
    cta: 'Next',
  },
  10: {
    selector: '[data-testid="ep-publish-btn"]',
    text: 'Click Publish to make it live on Karmana',
    cta: 'Close & Return',
  },
};

function calcTooltipPos(rect) {
  const gap = 14;
  const m = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const h = 150;
  const pad = 6;
  const r = { l: rect.left - pad, t: rect.top - pad, r: rect.right + pad, b: rect.bottom + pad };

  if (r.r + gap + TOOLTIP_W + m < vw)  return { left: r.r + gap, top: Math.max(m, Math.min(r.t, vh - h - m)) };
  if (r.l - gap - TOOLTIP_W - m > 0)   return { left: r.l - gap - TOOLTIP_W, top: Math.max(m, Math.min(r.t, vh - h - m)) };
  if (r.b + gap + h + m < vh)          return { left: Math.max(m, Math.min(r.l, vw - TOOLTIP_W - m)), top: r.b + gap };
  return { left: Math.max(m, Math.min(r.l, vw - TOOLTIP_W - m)), top: Math.max(m, r.t - gap - h) };
}

export default function ActivityEditTutorial({ activityId, activitySlug, step, onStepChange }) {
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const cfg = STEP_CFG[step];

  useEffect(() => {
    setVisible(false);
    setRect(null);

    const primarySel = cfg?.selector;
    if (!primarySel) return;

    let raf;
    let scrollTimer;
    let found = false;

    const update = () => {
      const el = document.querySelector(primarySel);

      if (el && !found) {
        found = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        scrollTimer = setTimeout(() => setVisible(true), 650);
      }
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) setRect(r);
      }
      raf = requestAnimationFrame(update);
    };
    raf = requestAnimationFrame(update);

    return () => { cancelAnimationFrame(raf); clearTimeout(scrollTimer); };
  }, [step, cfg?.selector]); // eslint-disable-line

  const handleNext = async () => {
    if (step < 10) {
      onStepChange(step + 1);
    } else {
      // Step 10 — track, close tab, notify original tab
      try {
        const stored = JSON.parse(localStorage.getItem('karmana_tutorial') || '{}');
        await trackTutorialSession({
          trigger_type: stored.triggerType || 'first_landing',
          max_step_reached: 10,
          completed: true,
          started_at: stored.startedAt ? new Date(stored.startedAt).toISOString() : new Date().toISOString(),
          steps: stored.steps || [],
        });
      } catch {}
      localStorage.removeItem('karmana_tutorial');
      if (window.opener) {
        window.opener.postMessage({ type: 'karmana_tutorial_complete', activityId, activitySlug }, '*');
        window.opener.focus();
      }
      window.close();
    }
  };

  if (!visible || !rect) return null;

  const tooltipPos = calcTooltipPos(rect);
  const pad = 6;

  return (
    <>
      <style>{`@keyframes kt-ring{0%{transform:scale(.5);opacity:.7}100%{transform:scale(2.2);opacity:0}}`}</style>

      {/* Full-page click blocker — prevents any form interaction during tutorial */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, zIndex: 9994,
          background: 'transparent', cursor: 'default',
        }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      />

      {/* Spotlight */}
      <div
        aria-hidden="true"
        data-testid="tutorial-spotlight"
        style={{
          position: 'fixed', zIndex: 9998,
          top: rect.top - pad, left: rect.left - pad,
          width: rect.width + pad * 2, height: rect.height + pad * 2,
          borderRadius: 10,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.28), 0 0 0 1.5px rgba(255,255,255,0.15)',
          background: 'rgba(255,255,255,0.04)',
          pointerEvents: 'none',
        }}
      />

      {/* Tooltip card */}
      <div
        style={{ position: 'fixed', zIndex: 10000, width: TOOLTIP_W, ...tooltipPos }}
        data-testid={`tutorial-step-card-${step}`}
      >
        <div style={{
          background: '#fff', borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.09)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 13px 8px', borderBottom: '1px solid #f3f4f6',
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '0.04em' }}>
              STEP {step} / {TOTAL_STEPS}
            </span>
          </div>
          <div style={{ padding: '11px 13px 13px' }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.5, marginBottom: 12 }}>
              {cfg?.text}
            </p>
            <button
              onClick={handleNext}
              data-testid={`tutorial-next-${step}`}
              style={{
                width: '100%', padding: '8px 0', background: '#111827',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {cfg?.cta}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
